import type {BarEvent} from "@uptime-com/uptimebar";
import {BarEventStatus} from "@uptime-com/uptimebar";
import type {PublicComponentStatusInfo, PublicStatusPageDowntime, PublicStatusPageIncident} from "../types.ts";

const STATUS_TO_BAR_EVENT: Record<string, BarEventStatus> = {
  "major-outage": BarEventStatus.MAJOR_OUTAGE,
  "partial-outage": BarEventStatus.PARTIAL_OUTAGE,
  "degraded-performance": BarEventStatus.DEGRADED_PERFORMANCE,
  notification: BarEventStatus.NOTIFICATION,
  "under-maintenance": BarEventStatus.UNDER_MAINTENANCE,
  operational: BarEventStatus.OPERATIONAL,
};

export const buildComponentStatusRank = (metadata: PublicComponentStatusInfo[]): Record<string, number> => {
  const map: Record<string, number> = {};
  for (const m of metadata) {
    map[m.value] = m.rank;
  }
  return map;
};

const getWorstStatus = (affectedComponents: {status: string}[], statusRank: Record<string, number>): BarEventStatus => {
  if (affectedComponents.length === 0) return BarEventStatus.MAJOR_OUTAGE;

  let worstStatus = "operational";
  let worstRank = statusRank["operational"] ?? 6;

  for (const comp of affectedComponents) {
    const rank = statusRank[comp.status] ?? 6;
    if (rank < worstRank) {
      worstRank = rank;
      worstStatus = comp.status;
    }
  }

  return STATUS_TO_BAR_EVENT[worstStatus] ?? BarEventStatus.MAJOR_OUTAGE;
};

// Severity rank for bar event statuses (lower = more severe)
const BAR_STATUS_SEVERITY: Record<string, number> = {
  [BarEventStatus.MAJOR_OUTAGE]: 1,
  [BarEventStatus.PARTIAL_OUTAGE]: 2,
  [BarEventStatus.DEGRADED_PERFORMANCE]: 3,
  [BarEventStatus.UNDER_MAINTENANCE]: 4,
  [BarEventStatus.NOTIFICATION]: 5,
  [BarEventStatus.OPERATIONAL]: 6,
  [BarEventStatus.NO_DATA]: 7,
};

/**
 * Merge overlapping events so each time range only has the most severe status.
 * Uses a sweep-line approach: collect all start/end boundaries, sort them,
 * and for each interval track the worst active status.
 */
const mergeOverlappingEvents = (events: BarEvent[]): BarEvent[] => {
  if (events.length <= 1) return events;

  // Collect boundary points
  const boundaries: {time: number; type: "start" | "end"; status: BarEventStatus}[] = [];
  for (const ev of events) {
    boundaries.push({time: ev.start.getTime(), type: "start", status: ev.status});
    boundaries.push({time: (ev.end ?? ev.start).getTime(), type: "end", status: ev.status});
  }

  // Sort by time, with 'start' before 'end' at same time
  boundaries.sort((a, b) => a.time - b.time || (a.type === "start" ? -1 : 1));

  const activeStatuses = new Map<BarEventStatus, number>(); // status -> count
  const merged: BarEvent[] = [];
  let prevTime = boundaries[0].time;

  for (const point of boundaries) {
    if (point.time > prevTime && activeStatuses.size > 0) {
      // Find worst active status
      let worstStatus = BarEventStatus.OPERATIONAL;
      let worstRank = Infinity;
      for (const [status] of activeStatuses) {
        const rank = BAR_STATUS_SEVERITY[status] ?? 6;
        if (rank < worstRank) {
          worstRank = rank;
          worstStatus = status;
        }
      }

      // Extend previous event if same status, otherwise create new
      const last = merged[merged.length - 1];
      if (last && last.status === worstStatus && last.end?.getTime() === prevTime) {
        last.end = new Date(point.time);
      } else {
        merged.push({
          start: new Date(prevTime),
          end: new Date(point.time),
          status: worstStatus,
        });
      }
    }

    if (point.type === "start") {
      activeStatuses.set(point.status, (activeStatuses.get(point.status) ?? 0) + 1);
    } else {
      const count = activeStatuses.get(point.status) ?? 1;
      if (count <= 1) {
        activeStatuses.delete(point.status);
      } else {
        activeStatuses.set(point.status, count - 1);
      }
    }

    prevTime = point.time;
  }

  return merged;
};

/**
 * Status page and history bars: everything the incidents do not cover reads as operational.
 *
 * These bars answer "was the service affected?", not "did the check report?" — so a period with
 * no incident is up, and only the check drill-down (which has real per-interval coverage from the
 * backend) distinguishes unknown from up. An unresolved incident therefore runs to the end of the
 * window instead of stopping at now and letting the operational filler paint the rest green.
 */
export const generateEventsFromIncidents = (
  incidents: PublicStatusPageIncident[],
  statusMetadata: PublicComponentStatusInfo[],
  start: Date,
  end: Date
): BarEvent[] => {
  const statusRank = buildComponentStatusRank(statusMetadata);
  const startMs = start.getTime();
  const endMs = end.getTime();

  const incidentEvents: BarEvent[] = [];

  for (const inc of incidents) {
    if (!inc.include_in_global_metrics) continue;

    const incStartMs = inc.starts_at ? new Date(inc.starts_at).getTime() : startMs;
    const incEndMs = inc.ends_at ? new Date(inc.ends_at).getTime() : endMs;

    const clampedStart = Math.max(incStartMs, startMs);
    const clampedEnd = Math.min(incEndMs, endMs);

    if (clampedStart >= clampedEnd) continue;

    incidentEvents.push({
      start: new Date(clampedStart),
      end: new Date(clampedEnd),
      status: getWorstStatus(inc.affected_components, statusRank),
    });
  }

  // Merge overlapping incidents so each time slice shows only the worst severity
  return mergeOverlappingEvents(incidentEvents);
};

/**
 * Status page and history bars: everything the downtimes do not cover reads as operational.
 *
 * Same contract as generateEventsFromIncidents — no downtime means up, and an unresolved outage
 * runs to the end of the window so the remainder is never painted green behind an ongoing outage.
 */
export const generateEventsFromDowntimes = (
  downtimes: PublicStatusPageDowntime[],
  start: Date,
  end: Date
): BarEvent[] => {
  // Clamp in milliseconds, not whole seconds. Truncating the window end to a second left the red
  // event short of a range ending at 23:59:59.999, and that sub-second gap fell through to the
  // operational filler as a green sliver at the very end of the bar.
  const startMs = start.getTime();
  const endMs = end.getTime();

  const events: BarEvent[] = [];

  for (const downtime of downtimes) {
    const downtimeEndMs = downtime.end_ts == null ? endMs : downtime.end_ts * 1000;
    const clampedStart = Math.max(downtime.start_ts * 1000, startMs);
    const clampedEnd = Math.min(downtimeEndMs, endMs);

    if (clampedStart >= clampedEnd) continue;

    events.push({
      start: new Date(clampedStart),
      end: new Date(clampedEnd),
      status: BarEventStatus.MAJOR_OUTAGE,
    });
  }

  return events;
};

/**
 * Given a list of incidents and a time window, return the incidents
 * that overlap with [periodStart, periodEnd].
 */
export const getIncidentsInPeriod = (
  incidents: PublicStatusPageIncident[],
  periodStart: Date,
  periodEnd: Date
): PublicStatusPageIncident[] => {
  const startMs = periodStart.getTime();
  const endMs = periodEnd.getTime();

  return incidents.filter((inc) => {
    const incStart = inc.starts_at ? new Date(inc.starts_at).getTime() : 0;
    const incEnd = inc.ends_at ? new Date(inc.ends_at).getTime() : endMs;
    return incStart < endMs && incEnd > startMs;
  });
};

/**
 * For a list of incidents overlapping a period, compute the merged affected
 * components with worst-severity status per component.
 */
export const getAffectedComponentsInPeriod = (
  incidents: PublicStatusPageIncident[],
  statusRank: Record<string, number>
): Record<string, string> => {
  const components: Record<string, string> = {};

  for (const inc of incidents) {
    for (const afc of inc.affected_components) {
      const existing = components[afc.name];
      if (!existing || (statusRank[existing] ?? 6) > (statusRank[afc.status] ?? 6)) {
        components[afc.name] = afc.status;
      }
    }
  }

  return components;
};

/**
 * Given overlapping incidents and a time window, compute total downtime
 * in seconds within that window (merging overlapping ranges).
 */
export const computeDowntimeInPeriod = (
  incidents: PublicStatusPageIncident[],
  periodStart: Date,
  periodEnd: Date
): number => {
  const startMs = periodStart.getTime();
  const endMs = periodEnd.getTime();

  const nowMs = Date.now();

  const ranges = incidents
    .filter((inc) => inc.include_in_global_metrics)
    .map((inc) => {
      const s = Math.max(inc.starts_at ? new Date(inc.starts_at).getTime() : 0, startMs);
      const incEnd = inc.ends_at ? new Date(inc.ends_at).getTime() : Math.min(nowMs, endMs);
      const e = Math.min(incEnd, endMs);
      return [s, e] as [number, number];
    })
    .filter(([s, e]) => s < e)
    .sort((a, b) => a[0] - b[0]);

  let total = 0;
  let currentEnd = 0;

  for (const [s, e] of ranges) {
    const effectiveStart = Math.max(s, currentEnd);
    if (effectiveStart < e) {
      total += e - effectiveStart;
      currentEnd = Math.max(currentEnd, e);
    }
  }

  return Math.floor(total / 1000);
};

export const computeCheckBasedDowntimeInPeriod = (
  downtimeHistory: PublicStatusPageDowntime[],
  periodStart: Date,
  periodEnd: Date
): number => {
  const startSec = Math.floor(periodStart.getTime() / 1000);
  const endSec = Math.floor(periodEnd.getTime() / 1000);
  const nowSec = Math.floor(Date.now() / 1000);
  const effectiveEndSec = Math.min(endSec, nowSec);
  if (effectiveEndSec <= startSec) return 0;

  const ranges = downtimeHistory
    .map((dt) => {
      const dtEnd = dt.end_ts ?? effectiveEndSec;
      return [Math.max(dt.start_ts, startSec), Math.min(dtEnd, effectiveEndSec)] as [number, number];
    })
    .filter(([s, e]) => s < e)
    .sort((a, b) => a[0] - b[0]);

  let merged = 0;
  let currentEnd = 0;
  for (const [s, e] of ranges) {
    const effectiveStart = Math.max(s, currentEnd);
    if (effectiveStart < e) {
      merged += e - effectiveStart;
      currentEnd = Math.max(currentEnd, e);
    }
  }
  return merged;
};

/**
 * Formats a duration (in seconds) into its two largest units, rounding to the nearest minute so the
 * new status page matches the legacy page (which rounds) instead of truncating. Multi-day durations
 * round to the nearest hour, mirroring the old `Formatter.shortDuration`.
 */
export const formatDurationRounded = (seconds: number): string => {
  if (seconds === 0) return "0s";
  const rounded = seconds >= 86400 ? Math.round(seconds / 3600) * 3600 : Math.round(seconds / 60) * 60;
  if (rounded === 0) return "< 1m";
  const totalMinutes = rounded / 60;
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

export const formatDowntimeDuration = (seconds: number): string => {
  if (seconds === 0) return "0s";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
  return parts.slice(0, 2).join(" ");
};
